import { useCallback, useEffect, useMemo, useState } from 'react'
import { View, Text, Linking, Share } from 'react-native'
import { Redirect, useFocusEffect, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { Clock, Minus, Plus } from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import {
  CheckoutEditorialRow,
  CheckoutPaymentRail,
  CheckoutSummaryColumn,
} from '@/components/booking/CheckoutEditorialLayout'
import { CheckoutVehicleHero } from '@/components/booking/CheckoutVehicleHero'
import { TourCustomCheckoutHero } from '@/components/tours/TourCustomCheckoutHero'
import { CheckoutPayFooter } from '@/components/payments/CheckoutPayFooter'
import { CheckoutMethodPicker } from '@/components/payments/CheckoutMethodPicker'
import { CheckoutSecureHeader } from '@/components/payments/CheckoutSecureHeader'
import { CheckoutSplitBoard } from '@/components/payments/CheckoutSplitBoard'
import {
  ToursCardForm,
  EMPTY_TOURS_CARD,
  toursCardIsValid,
  toursCardToChargeInput,
  type ToursCardValue,
} from '@/components/tours/ToursCardForm'
import { useToursCheckoutStore } from '@/lib/stores/tours-checkout.store'
import { usePayTour, useConfirmTour, useCreateSplit } from '@/lib/hooks/use-tours'
import { useAuth } from '@/lib/hooks/use-auth'
import { checkoutPayerFromProfile } from '@/lib/payments/charge-result'
import {
  guestChargeCents,
  splitMarketplaceCents,
  type CheckoutPaymentMethod,
} from '@/lib/payments/processor-fee'
import { isPaypalOffered } from '@/lib/payments/paypal-offered'
import { isPreviewToursMoney, peekPreviewSplit } from '@/lib/tours/preview-payments'
import { toursChargeFailureI18nKey, toursPaymentErrorKey } from '@/lib/tours/payment-errors'
import { useTheme } from '@/lib/hooks/use-theme'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import { useSemanticColors, INTERACTION, withAlpha } from '@/lib/theme/tokens'
import { FONTS, SPACING, RADIUS } from '@/lib/brand'
import { formatCurrency, formatPrice } from '@/lib/utils/format'
import { formatTourDeparture } from '@/lib/tours/format'
import { toursPerPersonFromVehicleCents } from '@/lib/tours/pricing'
import type { TourSplitResult } from '@/lib/types/tours.types'

const CONFIRM_POLLS = 8
const CONFIRM_POLL_MS = 2000

function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

export default function ToursCheckoutScreen() {
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { isDesktop, isTablet } = useBreakpoint()
  const isEditorial = isDesktop || isTablet
  const checkoutPadX = SPACING.lg
  const context = useToursCheckoutStore((s) => s.context)
  const paypalOffered = isPaypalOffered()

  const pay = usePayTour()
  const confirm = useConfirmTour()
  const createSplit = useCreateSplit()

  const [card, setCard] = useState<ToursCardValue>(EMPTY_TOURS_CARD)
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const { user, profile } = useAuth()
  const [method, setMethod] = useState<CheckoutPaymentMethod>('card')

  useEffect(() => {
    setCard((current) => {
      if (current.email !== '') return current
      if (user?.email) return { ...current, email: user.email }
      if (context && isPreviewToursMoney(context.booking.id)) {
        return { ...current, email: 'preview@tago.sv' }
      }
      return current
    })
  }, [user?.email, context])
  const [mode, setMode] = useState<'full' | 'split'>(() =>
    context && isPreviewToursMoney(context.booking.id) && peekPreviewSplit(context.booking.id)
      ? 'split'
      : 'full',
  )
  const [splitCount, setSplitCount] = useState(context?.booking.passengerCount ?? 2)
  const [splitResult, setSplitResult] = useState<TourSplitResult | null>(() =>
    context && isPreviewToursMoney(context.booking.id)
      ? peekPreviewSplit(context.booking.id)
      : null,
  )
  const [splitErrorKey, setSplitErrorKey] = useState<string | null>(null)

  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])

  useFocusEffect(
    useCallback(() => {
      if (!context || !isPreviewToursMoney(context.booking.id)) return
      const next = peekPreviewSplit(context.booking.id)
      if (next) {
        setSplitResult(next)
        setMode('split')
      }
    }, [context]),
  )

  const expiresAt = context?.booking.holdExpiresAt ?? context?.booking.expiresAt ?? null
  const remainingMs = expiresAt ? new Date(expiresAt).getTime() - now : null
  const holdExpired = remainingMs != null && remainingMs <= 0
  const holdSoon = remainingMs != null && remainingMs > 0 && remainingMs < 3 * 60 * 1000

  const total = useMemo(() => {
    if (!context) return 0
    const booking = context.booking
    if (typeof booking.totalCents === 'number') return booking.totalCents
    if (typeof booking.guestPriceCents === 'number') return booking.guestPriceCents
    return 0
  }, [context])

  const guestQuote = useMemo(
    () => guestChargeCents({ marketplaceCents: total, method }),
    [total, method],
  )
  const chargedCents = guestQuote.chargeCents
  const paypalDelta = Math.max(0, chargedCents - total)

  const perPersonCents = useMemo(
    () =>
      context && total > 0
        ? toursPerPersonFromVehicleCents(total, context.booking.passengerCount)
        : 0,
    [context, total],
  )

  const perShareCents = useMemo(() => {
    const shares = splitMarketplaceCents(total, splitCount)
    return shares[0] ?? 0
  }, [total, splitCount])

  const handleCreateSplit = async () => {
    if (!context) return
    setSplitErrorKey(null)
    try {
      const result = await createSplit.mutateAsync({
        bookingId: context.booking.id,
        participantCount: splitCount,
      })
      setSplitResult(result)
    } catch (err) {
      setSplitErrorKey(toursPaymentErrorKey((err as { code?: string }).code))
    }
  }

  const shareUrl = (url: string) => {
    void Share.share({ message: url }).catch(() => undefined)
  }

  if (!context) {
    return <Redirect href="/(tabs)/tours" />
  }

  const isPending = pay.isPending || confirm.isPending
  const paypalPayer = checkoutPayerFromProfile({
    email: card.email || user?.email,
    firstName: profile?.firstName,
    lastName: profile?.lastName,
    phone: profile?.phone,
  })
  const paypalReady = Boolean(paypalPayer.email.includes('@'))

  const submit = async () => {
    setErrorKey(null)
    if (method === 'card' && !toursCardIsValid(card)) {
      setErrorKey('tours.checkout.invalid_card')
      return
    }
    if (method === 'paypal' && !paypalReady) {
      setErrorKey('tours.checkout.email_required')
      return
    }
    const { card: cardInput, payer } = toursCardToChargeInput(card)
    const paypalFallback = {
      ...paypalPayer,
      firstName: paypalPayer.firstName || 'Tago',
      lastName: paypalPayer.lastName || 'Guest',
    }

    try {
      const result = await pay.mutateAsync({
        bookingId: context.booking.id,
        payer: method === 'paypal' ? paypalFallback : payer,
        card: method === 'card' ? cardInput : undefined,
        provider: method === 'paypal' ? 'paypal' : undefined,
      })

      if (result.status === 'captured') {
        router.replace('/(tabs)/tours/payment-complete?status=success')
        return
      }

      if (result.challenge) {
        await Linking.openURL(result.challenge.url).catch(() => undefined)
      }

      if (!result.transactionId) {
        router.replace('/(tabs)/tours/payment-complete?status=pending')
        return
      }

      for (let attempt = 0; attempt < CONFIRM_POLLS; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, CONFIRM_POLL_MS))
        const confirmation = await confirm.mutateAsync({
          bookingId: context.booking.id,
          transactionId: result.transactionId,
        })
        if (confirmation.status === 'captured') {
          router.replace('/(tabs)/tours/payment-complete?status=success')
          return
        }
        if (confirmation.status === 'failed') {
          router.replace('/(tabs)/tours/payment-complete?status=failed')
          return
        }
      }
      router.replace('/(tabs)/tours/payment-complete?status=pending')
    } catch (err) {
      setErrorKey(toursChargeFailureI18nKey(err))
    }
  }

  const payTitle =
    method === 'paypal'
      ? t('checkout.pay_with_paypal', { amount: formatCurrency(chargedCents) })
      : t('tours.checkout.pay', { amount: formatCurrency(chargedCents) })

  const railFooter =
    mode === 'full' || context.booking.passengerCount < 2 ? (
      <CheckoutPayFooter
        title={payTitle}
        onPress={() => void submit()}
        loading={isPending}
        disabled={holdExpired}
        testID="tours.checkout-pay"
        trustText={t('tours.checkout.secure_note')}
        errorText={errorKey ? t(errorKey) : null}
        errorTestID="tours.checkout-error"
        hintText={method === 'paypal' ? t('checkout.processor_fee_hint') : null}
      />
    ) : splitResult ? (
      <CheckoutPayFooter
        title={t('tours.checkout.pay_my_share', {
          amount: formatPrice(splitResult.shares[0]?.amountCents ?? 0),
        })}
        onPress={() => router.push(`/split/${splitResult.shares[0]?.shareToken}`)}
        testID="tours.checkout-pay-share"
        trustText={t('tours.checkout.secure_note')}
      />
    ) : (
      <CheckoutPayFooter
        title={t('tours.checkout.create_split')}
        onPress={() => void handleCreateSplit()}
        loading={createSplit.isPending}
        disabled={holdExpired}
        testID="tours.checkout-create-split"
        trustText={t('tours.checkout.secure_note')}
        errorText={splitErrorKey ? t(splitErrorKey) : null}
        errorTestID="tours.checkout-split-error"
      />
    )

  return (
    <ScreenContainer variant="full-bleed" scroll={false}>
      <View style={{ flex: 1, width: '100%' }}>
        <KeyboardAwareView
          includeTopInset={false}
          style={{ flex: 1, width: '100%' }}
          contentContainerStyle={{
            paddingHorizontal: isEditorial ? 0 : checkoutPadX,
            paddingTop: SPACING.sm,
            paddingBottom: SPACING['2xl'],
          }}
          showsVerticalScrollIndicator={false}
        >
          <ScreenHeader
            title={t('tours.checkout.title')}
            backTestID="tours.checkout-back"
            onBackPress={() => router.back()}
            style={{
              paddingHorizontal: isEditorial ? checkoutPadX : 0,
              paddingTop: SPACING.sm,
              paddingBottom: SPACING.sm,
            }}
          />

          <View style={{ paddingHorizontal: isEditorial ? checkoutPadX : 0 }}>
            {remainingMs != null && !holdExpired ? (
              <View
                style={{
                  marginTop: SPACING.sm,
                  flexDirection: 'row',
                  alignItems: 'center',
                  alignSelf: 'flex-start',
                  paddingHorizontal: SPACING.md,
                  paddingVertical: SPACING.xs + 2,
                  borderRadius: RADIUS.full,
                  backgroundColor: holdSoon
                    ? withAlpha(colors.warning, 0.14)
                    : colors.surfaceSubtle,
                }}
                testID="tours.checkout-countdown"
              >
                <Clock
                  size={14}
                  color={holdSoon ? colors.warning : colors.textAccent}
                  strokeWidth={1.8}
                />
                <Text
                  style={{
                    marginLeft: 6,
                    fontFamily: FONTS.body,
                    fontSize: 13,
                    color: holdSoon ? colors.warning : colors.textSecondary,
                  }}
                >
                  {holdSoon
                    ? t('tours.checkout.hold_soon', { time: formatCountdown(remainingMs) })
                    : t('tours.checkout.hold_remaining', { time: formatCountdown(remainingMs) })}
                </Text>
              </View>
            ) : holdExpired ? (
              <View
                style={{
                  marginTop: SPACING.sm,
                  flexDirection: 'row',
                  alignItems: 'center',
                  padding: SPACING.md,
                  borderRadius: RADIUS.lg,
                  backgroundColor: withAlpha(colors.textError, 0.1),
                }}
                testID="tours.checkout-hold-expired"
              >
                <Clock size={16} color={colors.textError} strokeWidth={1.8} />
                <Text
                  style={{
                    marginLeft: 6,
                    flex: 1,
                    fontFamily: FONTS.body,
                    fontSize: 13,
                    color: colors.textError,
                  }}
                >
                  {t('tours.errors.hold_expired')}
                </Text>
              </View>
            ) : null}
          </View>

          <CheckoutEditorialRow
            editorial={isEditorial}
            padX={checkoutPadX}
            testID="tours.checkout-editorial-layout"
          >
            <CheckoutSummaryColumn editorial={isEditorial}>
              <View style={{ marginTop: SPACING.md, gap: SPACING.md }}>
                {context.customTour ? (
                  <TourCustomCheckoutHero
                    title={context.title}
                    originLabel={context.originLabel}
                    destinationLabel={context.destinationLabel}
                    dateLabel={formatTourDeparture(context.startsAt, i18n.language)}
                    vehicleLabel={context.customTour.vehicleLabel}
                    stopCount={context.customTour.stopCount}
                  />
                ) : (
                  <CheckoutVehicleHero
                    title={context.title}
                    dateLabel={formatTourDeparture(context.startsAt, i18n.language)}
                    imageUrl={context.coverImageUrl}
                    eyebrow={t('tours.route', {
                      origin: context.originLabel,
                      destination: context.destinationLabel,
                    })}
                  />
                )}
                <View style={{ gap: SPACING.xs }}>
                  <SummaryRow
                    label={t('tours.passengers')}
                    value={String(context.booking.passengerCount)}
                  />
                  {context.luggageLabels.length > 0 ? (
                    <SummaryRow
                      label={t('tours.luggage')}
                      value={context.luggageLabels.join(' · ')}
                    />
                  ) : null}
                </View>
                <View
                  style={{
                    paddingTop: SPACING.md,
                    borderTopWidth: 1,
                    borderTopColor: colors.border,
                    gap: SPACING.xs,
                  }}
                >
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Text
                      style={{ fontFamily: FONTS.body, fontSize: 15, color: colors.textSecondary }}
                    >
                      {t('tours.checkout.total')}
                    </Text>
                    <Text
                      style={{ fontFamily: FONTS.bodyBold, fontSize: 24, color: colors.textAccent }}
                      testID="tours.checkout-total"
                    >
                      {formatCurrency(chargedCents)}
                    </Text>
                  </View>
                  {paypalDelta > 0 ? (
                    <Text
                      style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textMuted }}
                      testID="tours.checkout-paypal-fee"
                    >
                      {t('checkout.paypal_fee_line')} · {formatPrice(paypalDelta)}
                    </Text>
                  ) : null}
                  {perPersonCents > 0 ? (
                    <Text
                      style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textMuted }}
                      testID="tours.checkout-per-person"
                    >
                      {formatPrice(perPersonCents)} {t('tours.per_person')}
                    </Text>
                  ) : null}
                </View>
              </View>
            </CheckoutSummaryColumn>

            <CheckoutPaymentRail
              editorial={isEditorial}
              wide={isDesktop}
              testID="tours.checkout-payment-rail"
              footer={railFooter}
            >
              {context.booking.passengerCount >= 2 ? (
                <View
                  style={{
                    marginTop: isEditorial ? 0 : SPACING.xl,
                    flexDirection: 'row',
                    backgroundColor: colors.surfaceSubtle,
                    borderRadius: RADIUS.full,
                    padding: 3,
                  }}
                >
                  {(['full', 'split'] as const).map((m) => (
                    <Pressable
                      key={m}
                      onPress={() => setMode(m)}
                      accessibilityRole="tab"
                      accessibilityState={{ selected: mode === m }}
                      testID={
                        m === 'full' ? 'tours.checkout-mode-full' : 'tours.checkout-mode-split'
                      }
                      style={{
                        flex: 1,
                        minHeight: INTERACTION.minTouchTarget - 6,
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: RADIUS.full,
                        backgroundColor: mode === m ? colors.surface : 'transparent',
                      }}
                    >
                      <Text
                        style={{
                          fontFamily: FONTS.body,
                          fontSize: 14,
                          color: mode === m ? colors.textPrimary : colors.textSecondary,
                        }}
                      >
                        {m === 'full'
                          ? t('tours.checkout.pay_full')
                          : t('tours.checkout.pay_split')}
                      </Text>
                    </Pressable>
                  ))}
                </View>
              ) : null}

              {mode === 'full' || context.booking.passengerCount < 2 ? (
                <View style={{ marginTop: SPACING.lg, gap: SPACING.md }}>
                  <CheckoutSecureHeader
                    title={
                      method === 'paypal'
                        ? t('checkout.paypal_secure_title')
                        : t('checkout.secure_payment_title')
                    }
                    description={
                      method === 'paypal'
                        ? t('checkout.paypal_secure_desc')
                        : t('checkout.secure_payment_desc')
                    }
                  />
                  {paypalOffered ? (
                    <CheckoutMethodPicker
                      method={method}
                      onChange={setMethod}
                      title={t('checkout.payment_method')}
                      cardLabel={t('checkout.card')}
                      paypalLabel={t('checkout.paypal')}
                    />
                  ) : null}

                  {method === 'card' ? (
                    <ToursCardForm value={card} onChange={setCard} />
                  ) : (
                    <Text
                      style={{
                        fontFamily: FONTS.body,
                        fontSize: 14,
                        lineHeight: 20,
                        color: colors.textSecondary,
                      }}
                    >
                      {t('checkout.paypal_desc')}
                    </Text>
                  )}
                </View>
              ) : (
                <View style={{ marginTop: SPACING.lg, gap: SPACING.md }}>
                  <Text
                    style={{
                      fontFamily: FONTS.bodyBold,
                      fontSize: 17,
                      color: colors.textPrimary,
                    }}
                  >
                    {t('tours.checkout.how_many_pay')}
                  </Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Pressable
                      onPress={() => setSplitCount((c) => Math.max(2, c - 1))}
                      disabled={splitCount <= 2}
                      accessibilityRole="button"
                      accessibilityLabel={t('tours.decrease_passengers')}
                      testID="tours.split-minus"
                      style={{
                        width: INTERACTION.minTouchTarget,
                        height: INTERACTION.minTouchTarget,
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: RADIUS.full,
                        borderWidth: 1,
                        borderColor: colors.border,
                        backgroundColor: colors.surface,
                      }}
                    >
                      <Minus
                        size={18}
                        color={splitCount <= 2 ? colors.textDisabled : colors.textPrimary}
                        strokeWidth={2}
                      />
                    </Pressable>
                    <Text
                      style={{
                        minWidth: 64,
                        textAlign: 'center',
                        fontFamily: FONTS.bodyBold,
                        fontSize: 17,
                        color: colors.textPrimary,
                      }}
                      testID="tours.split-count"
                    >
                      {splitCount}
                    </Text>
                    <Pressable
                      onPress={() =>
                        setSplitCount((c) => Math.min(context.booking.passengerCount, c + 1))
                      }
                      disabled={splitCount >= context.booking.passengerCount}
                      accessibilityRole="button"
                      accessibilityLabel={t('tours.increase_passengers')}
                      testID="tours.split-plus"
                      style={{
                        width: INTERACTION.minTouchTarget,
                        height: INTERACTION.minTouchTarget,
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: RADIUS.full,
                        borderWidth: 1,
                        borderColor: colors.border,
                        backgroundColor: colors.surface,
                      }}
                    >
                      <Plus
                        size={18}
                        color={
                          splitCount >= context.booking.passengerCount
                            ? colors.textDisabled
                            : colors.textPrimary
                        }
                        strokeWidth={2}
                      />
                    </Pressable>
                    <Text
                      style={{
                        marginLeft: SPACING.md,
                        fontFamily: FONTS.body,
                        fontSize: 13,
                        color: colors.textMuted,
                      }}
                    >
                      {t('tours.max', { count: context.booking.passengerCount })}
                    </Text>
                  </View>
                  <Text
                    style={{
                      fontFamily: FONTS.bodyBold,
                      fontSize: 17,
                      color: colors.textAccent,
                    }}
                    testID="tours.split-per-share"
                  >
                    {t('tours.checkout.each_pays', { amount: formatPrice(perShareCents) })}
                  </Text>

                  {splitResult ? (
                    <View style={{ gap: SPACING.md }}>
                      <View>
                        <Text
                          style={{
                            fontFamily: FONTS.bodyBold,
                            fontSize: 17,
                            color: colors.textPrimary,
                          }}
                        >
                          {t('tours.checkout.split_created')}
                        </Text>
                        <Text
                          style={{
                            marginTop: 2,
                            fontFamily: FONTS.body,
                            fontSize: 13,
                            lineHeight: 19,
                            color: colors.textSecondary,
                          }}
                        >
                          {t('tours.checkout.split_created_hint')}
                        </Text>
                        <View style={{ marginTop: SPACING.sm }}>
                          <CheckoutSplitBoard
                            shares={splitResult.shares}
                            onShare={shareUrl}
                            onOpen={(token) => router.push(`/split/${token}`)}
                            shareLabel={t('tours.checkout.share')}
                            openLabel={t('tours.checkout.pay_my_share', {
                              amount: formatPrice(splitResult.shares[0]?.amountCents ?? 0),
                            })}
                            partLabel={(n) => t('tours.checkout.share_part', { n })}
                            paidLabel={t('tours.split.paid_title')}
                            pendingLabel={t('tours.booking_status.awaiting_payment')}
                          />
                        </View>
                      </View>
                    </View>
                  ) : null}
                </View>
              )}
            </CheckoutPaymentRail>
          </CheckoutEditorialRow>
        </KeyboardAwareView>
      </View>
    </ScreenContainer>
  )
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  return (
    <View
      style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}
    >
      <Text style={{ fontFamily: FONTS.body, fontSize: 14, color: colors.textMuted }}>{label}</Text>
      <Text
        style={{
          flex: 1,
          marginLeft: SPACING.md,
          textAlign: 'right',
          fontFamily: FONTS.body,
          fontSize: 14,
          color: colors.textPrimary,
        }}
      >
        {value}
      </Text>
    </View>
  )
}
